import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Chip, DChip, Icon, Pane, PaneHead } from '@/components/kit';
import { errorText, useTechActions, type TechTask } from '../api';
import { drName, eyeText, mmss, secondsUntil, sendsTo, shortName, useNow, valuesLine } from '../format';
import { isClosed, type Item } from '../model';
import { CantModal } from './CantModal';
import { DeviceCard } from './DeviceCard';

/** Where the send lands — the board's "Sends to:" line. */
export function sendsToLine(t: TechTask, bundle = false): string {
  if (t.testOnly) return `Sends to: the request → ${sendsTo(t)} gets the result · the patient can go`;
  const dr = drName(t.requestedBy) || 'the doctor';
  const tail = bundle || t.bundle
    ? ' · each step sends as it finishes; the bundle closes itself'
    : `${t.priority !== 'routine' ? ', flagged urgent' : ''} · the bill line names you`;
  return `Sends to: the request → ${t.visitOpen ? 'the open visit' : 'the visit'} → ${dr}’s rail${tail}`;
}

/** Open a test: the capture for a file, the values form for numbers. */
export function useOpenTest() {
  const navigate = useNavigate();
  const act = useTechActions();
  return (t: TechTask) => {
    const go = () => navigate(`/technician/worklist/${t.id}`);
    if (t.state === 'open' && t.lane !== 'waiting') {
      act.start.mutate(t.id, { onSuccess: go, onError: (e) => toast.error(errorText(e)) });
    } else go();
  };
}

function CallCard({ t, sittingMin }: { t: TechTask; sittingMin: number | null }) {
  const act = useTechActions();
  const she = t.patient?.sex === 'F' ? 'she has' : t.patient?.sex === 'M' ? 'he has' : 'they have';
  return (
    <div className="card card-flat">
      <p className="t-ovl mut">Not in the room</p>
      <p className="t-sec sec mt8">
        {sittingMin ? `≈${sittingMin} min of machine time is booked — ` : ''}an empty chair is the bottleneck, not a busy
        machine. Nothing blocks the start; {she} to be called.
      </p>
      <p className="mt8">
        <DChip kind="outline" onClick={() => act.call.mutate(t.id, {
          onSuccess: () => toast.success(`The desk is calling ${shortName(t.patient) || 'the patient'}`),
          onError: (e) => toast.error(errorText(e)),
        })}>
          <Icon name="speakerphone" size="sm" /> Call the patient
        </DChip>
      </p>
    </div>
  );
}

/**
 * THE DOCKED PANE — the order you picked, or the bundle as its SEQUENCE
 * (fields before dilation, OCT after drops). It follows the selection; a lane
 * click that drops the selection says so rather than keeping a row the new
 * lane does not draw.
 */
export function WorklistPane({ item }: { item: Item | null }) {
  const open = useOpenTest();
  const navigate = useNavigate();
  const [cant, setCant] = useState<TechTask | null>(null);
  const now = useNow(Boolean(item?.tasks.some((t) => t.waiting?.until)));

  if (!item) {
    return (
      <Pane>
        <div className="emptystate">
          <p className="t-sec">Nothing to dock — this panel follows the order you pick.</p>
        </div>
      </Pane>
    );
  }

  const t = item.next;
  const cantModal = cant ? <CantModal task={cant} open onClose={() => setCant(null)} /> : null;

  if (item.bundle) {
    const done = item.tasks.filter((x) => x.state === 'done').length;
    const minutes = item.tasks.reduce((s, x) => s + (x.takesMin ?? 0), 0) || null;
    const allClosed = item.tasks.every(isClosed);
    return (
      <Pane
        head={(
          <PaneHead
            overline="Selected order · bundle"
            title={`${shortName(t.patient)} — bundle of ${item.tasks.length}`}
            sub={['one sitting', drName(t.requestedBy), minutes ? `≈${minutes} min` : ''].filter(Boolean).join(' · ')}
          />
        )}
        foot={allClosed ? null : item.next.lane === 'waiting' ? (
          <button type="button" className="btn btn-warnline grow" onClick={() => setCant(item.next)}>Can’t — reason ▾</button>
        ) : (
          <button type="button" className="btn btn-primary grow" onClick={() => open(item.next)}>
            {item.next.state === 'in_progress' ? 'Open' : 'Start'} · {item.next.bundle?.index ?? 1} {item.next.title}
          </button>
        )}
      >
        {item.tasks.map((x, i) => {
          const isNext = x.id === item.next.id && !allClosed;
          const left = secondsUntil(x.waiting?.until, now);
          return (
            <div key={x.id} className={`card${isNext ? ' card-acc' : ''}`}>
              <div className="flex" style={{ gap: 8 }}>
                <p className={`${isNext || x.state === 'done' ? 't-strong' : 't-body'} grow`}>{i + 1} · {x.title}</p>
                {x.state === 'done' ? <Chip kind="success" small>done ✓</Chip>
                  : x.state === 'cant' ? <Chip kind="warn" small>came back</Chip>
                    : x.state === 'in_progress' ? <Chip kind="soft" small>in progress · {x.mine ? 'you' : shortName(x.assignee)}</Chip>
                      : x.lane === 'waiting' ? <Chip kind="warn" small><Icon name="clock" size="sm" /> {x.waiting?.label}{left != null ? ` ${mmss(left)}` : ''}</Chip>
                        : isNext ? <DChip kind="soft" onClick={() => open(x)}>Start</DChip>
                          : x.sequence === 'after_drops' ? <Chip kind="outline" small>after drops</Chip> : null}
              </div>
              {x.state === 'done' && x.valuesForm && x.values ? (
                <>
                  <p className="t-sec mut mt8">{valuesLine(x.values)}</p>
                  <p className="mt8"><DChip kind="outline" onClick={() => navigate(`/technician/worklist/${x.id}`)}>Open the values</DChip></p>
                </>
              ) : isNext && x.sequence === 'before_dilation' ? (
                <p className="t-sec mut mt8">before dilation — order matters</p>
              ) : null}
            </div>
          );
        })}
        <div className="card" style={{ borderColor: 'var(--success-600)' }}>
          <p className="t-sec grn w6">
            {done} of {item.tasks.length} done → each sends as it finishes; the bundle closes itself
          </p>
        </div>
        {t.arrived === false && !allClosed ? <CallCard t={t} sittingMin={minutes} /> : null}
        {cantModal}
      </Pane>
    );
  }

  const left = secondsUntil(t.waiting?.until, now);
  const eye = eyeText(t.laterality);
  return (
    <Pane
      head={(
        <PaneHead
          overline="Selected order"
          title={t.title}
          sub={[eye, drName(t.requestedBy) || (t.referrerName ?? ''), t.priority !== 'routine' ? t.priority : '', t.testOnly ? 'test-only' : '']
            .filter(Boolean).join(' · ')}
        />
      )}
      foot={isClosed(t) ? null : t.lane === 'waiting' ? (
        <button type="button" className="btn btn-warnline grow" onClick={() => setCant(t)}>Can’t — reason ▾</button>
      ) : (
        <button type="button" className="btn btn-primary grow" onClick={() => open(t)}>
          {t.state === 'in_progress' ? 'Open the capture' : t.repeatOfId ? 'Redo' : 'Start'}
        </button>
      )}
    >
      <div className="card">
        <p className="t-ovl mut">The doctor asked for</p>
        <p className="t-strong mt8">{t.title}{eye ? `, ${eye}` : ''}</p>
        {t.indication || t.priority !== 'routine' || t.visitOpen ? (
          <p className="t-sec sec mt8">
            {[t.indication ? `why: ${t.indication}` : '', [t.priority !== 'routine' ? t.priority : '', t.visitOpen ? 'visit open' : ''].filter(Boolean).join(' — ')]
              .filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </div>
      {t.repeatReason ? (
        <div className="card" style={{ borderColor: 'var(--danger-600)' }}>
          <p className="t-ovl mut">Sent back for a repeat</p>
          <p className="t-sec red mt8">{drName(t.requestedBy) || 'The doctor'}: “{t.repeatReason}”</p>
        </div>
      ) : null}
      {t.lane === 'waiting' ? (
        <div className="card card-warn">
          <p className="t-ovl mut">Waiting</p>
          <p className="t-sec sec mt8">
            {t.waiting?.label}{left != null ? ` — ${mmss(left)} left` : ''}. It can start when the clock runs out; hand it over and
            the clock keeps running.
          </p>
        </div>
      ) : null}
      {isClosed(t) ? (
        <div className="card">
          <p className="t-ovl mut">{t.state === 'done' ? 'Sent' : 'Came back'}</p>
          <p className="t-sec sec mt8">
            {t.state === 'done'
              ? t.closedElsewhere ? 'closed on the order itself' : `performed by ${t.completedBy ? shortName(t.completedBy) : 'you'}`
              : `${t.cantNote ? `“${t.cantNote}”` : ''}`}
          </p>
        </div>
      ) : <DeviceCard task={t} />}
      <div className="card card-acc">
        <p className="t-sec" style={{ color: 'var(--brand-600)' }}>{sendsToLine(t)}</p>
      </div>
      {t.arrived === false && !isClosed(t) && !t.testOnly ? <CallCard t={t} sittingMin={t.takesMin} /> : null}
      {cantModal}
    </Pane>
  );
}
